/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

import { getHeadersForMedium } from '../../asset/downloader/asset-downloader';
import Cleaner from '../../cleaner/cleaner';
import ContentInfoParser from '../info/content-info-parser';
import ErrorName from '../../error/error-name';
import FileSystemSpaceDetector from '../../file/system/file-system-space-detector';
import FileSystemUtil from '../../file/system/file-system-util';
import FileTransferUtil from '../../file/transfer/file-transfer-util';
import PromiseUtil from '../../promise/promise-util';

/**
 * @private
 * @import { Asset } from '../../asset/asset'
 * @import { IAssetDownloader } from '../../asset/downloader/asset-downloader'
 * @import BasePathResolver from '../../basepath/basepath'
 * @import { CleanerBeforeRemoveSomeAssetsEventListener } from '../../cleaner/cleaner'
 * @import { FileSystemManager, FileSystemUnit } from '../../file/system/'
 * @import FileSystemUnitFinder from '../../file-system-unit-finder/file-system-unit-finder'
 * @import { Logger } from '../../logger/logger'
 * @import { Medium } from '../../medium/medium'
 * @import Store from '../../store/store'
 * @import { ViewerRegistry } from '../../viewer/registry/'
 * @import { ViewerType } from '../../viewer/type/'
 * @import { LogicalUnitMapper } from '../../logical-unit-mapper/'
 * @import { ContentInfo } from '../info/content-info'
 * @import { Session } from '../../session/session'
 * @import { EventBus as IEventBus } from '../../event-bus'
 */

/**
 * @typedef {Pick<ContentInfo, 'communicatorSettings' | 'media'>} IContentInfo
 * @typedef {IEventBus<EventTypes>} EventBus
 * @typedef {Pick<Session, 'storage'>} ISession
 * @typedef {{ medium: Medium; viewerType: ViewerType | null; }[]} MediaWithViewerType
 * @typedef {typeof import('../../../config').default} Config
 */

// TODO #84 Přerušení stahování.
// - Downloader#cancel()
// - Zruší probíhající stahování.
// - Zde by se mělo pokusit přerušit aktuální stahování. Každopádně přerušit sérii.
// - Neměl by být vstupem downloadPromise, pokud by bylo v budoucnu více paralelních stahování?
// - Možná inspirace rozhraním $timeout: https://docs.angularjs.org/api/ng/service/$timeout
/**
 * Stahovač obsahu
 * @implements {EventBus}
 */
export default class ContentDownloader {
  /**
   * @param {IAssetDownloader} assetDownloader
   * @param {BasePathResolver} basePathResolver
   * @param {Config} config Konfigurace aplikace.
   * @param {FileSystemManager} fileSystemManager
   * @param {FileSystemUnitFinder} fileSystemUnitFinder
   * @param {Logger} logger
   * @param {LogicalUnitMapper} logicalUnitMapper
   * @param {Store} store Perzistentní read-write datový sklad s assety.
   * @param {ISession} session Sezení s nastavením zařízení.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   */
  constructor(
    assetDownloader,
    basePathResolver,
    config,
    fileSystemManager,
    fileSystemUnitFinder,
    logger,
    logicalUnitMapper,
    store,
    session,
    viewerRegistry
  ) {
    this.assetDownloader = assetDownloader;
    this.basePathResolver = basePathResolver;
    this.cleaner = new Cleaner(assetDownloader, basePathResolver, store);
    // TODO: Chtělo by předávat jen default size a nikoliv celý Config objekt!
    this.defaultSize = config.device.downloader.defaultSize;
    this.fileSystemManager = fileSystemManager;
    this.fileSystemUnitFinder = fileSystemUnitFinder;
    this.logger = logger;
    this.logicalUnitMapper = logicalUnitMapper;
    this.session = session;
    this.store = store;
    this.viewerRegistry = viewerRegistry;

    /** @type {EventEmitter<EventTypes>} */
    this.emitter = new EventEmitter();
    // Navázání naslouchání na instanci.
    this.onCleanerBeforeRemoveSomeAssets =
      this.onCleanerBeforeRemoveSomeAssets.bind(this);
    // Naslouchání událostem.
    this.cleaner.on(
      'cleanerBeforeRemoveSomeAssets',
      this.onCleanerBeforeRemoveSomeAssets
    );
  }

  /**
   * Uvolní všechny zdroje pro garbage collection.
   */
  destroy() {
    // TODO #84 Přerušení stahování.
    // - Půjde synchronně? Asi ne.
    // - Minimálně volat cancel.
    const { cleaner } = this;
    cleaner.off(
      'cleanerBeforeRemoveSomeAssets',
      this.onCleanerBeforeRemoveSomeAssets
    );
  }

  // TODO: Je otázka, když se tiše spolykají chyby, tak zda by nebylo lepší
  // vracet seznam stažených assetů a nestažených s patřičnými chybami.
  // Samozřejmě se stávající sémantikou, že chyba jednoho média nerejectne
  // tuto Promise jako celek a že se nic nezkouší znovu.
  // Tím pádem by bylo na volajícícm, co si s danými chybami udělá.
  /**
   * Stáhne soubory z nového obsahu.
   * Při paralelním stahování více obsahů, může dojít k problémům
   * v synchronizaci, např. paralelní stahování stejného souboru
   * do stejného umístění.
   * Proto se doporučuje tuto metodu vždy volat sériově.
   * @param {IContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise<Asset[]>} Seznam stažených assetů.
   */
  async download(contentInfo) {
    // TODO #84 Zvážit, jak signalizovat přerušení stahování:
    // - Místo assetIdArray vracet null.
    //   - Ignorovat rozestahované soubory?
    // - Vracet assetIdArray a příznak přerušení.
    //   - Rodič rozhodne co se soubory při přerušení.
    this.logger.debug('Downloader#download');

    const assetArray = [];
    const downloadAssetErrors = [];

    const mediaWithViewerType = contentInfo.media.map((medium) => ({
      medium,
      viewerType: this.viewerRegistry.find(medium.type)
    }));

    // TODO: Ideální by bylo kontrolovat i fyzickou přítomnost assetů v úložišti.
    const mediaToDownload = filterMediaToDownload(mediaWithViewerType);

    // Zachová první medium při výskytu duplicit.
    const deduplicatedMedia = filterDuplicatedMedia(mediaToDownload);

    // Media bez assetů.
    const mediaWithoutAsset = filterMediaWithoutAssets(deduplicatedMedia);

    // Media, která mají stažený asset a je u nich vyžadována kontrola verze.
    const mediaToCheckVersion =
      filterMediaWithAssetAndCheckVersion(deduplicatedMedia);

    // TODO: Ideální by bylo udělat HEAD pro všechny media (to zatím nechceme), která se budou stahovat kvůli tomu,
    // aby se získala skutečná velikost souboru pro kontrolu místa na disku.
    const outdatedMedia = await mustFilterOutdatedMedia(
      mediaToCheckVersion,
      this.logger
    );

    const donwnloadMediaFunc = [
      // Media, která nemají asset se musí stáhnout.
      ...mediaWithoutAsset,
      // Media, která nejsou aktuální.
      ...outdatedMedia
    ].map(({ medium, viewerType }, idx) => async () => {
      try {
        const asset = await this.downloadAsset(
          contentInfo,
          medium,
          // TODO #555: Filter výše neumí zohlednit, že se udělal filtr na non-nil položky,
          // takže je nejjednodušší to přetypovat až zde ručně.
          /** @type {ViewerType} */ (viewerType)
        );
        // Úspěšně stažený asset se přidá do výstupu.
        assetArray.push(asset);
      } catch (error) {
        // TODO #84 Zde by se mělo kontrolovat, jestli nebylo vyžádáno přerušení.
        // Pokud něco selže, tak se přeskočí na další médium.

        // Nenalezení jednotky nechceme logovat
        if (/** @type {Error} */ (error).name !== ErrorName.NotFoundError) {
          const downloadAssetError = new Error(
            `${medium.id}: ${medium.url}: ${String(error)}`
          );
          // V tomto případě nechceme posílat stack trace.
          downloadAssetError.stack = '';
          downloadAssetErrors.push(downloadAssetError);
        }
      } finally {
        // Vyvolání události po zpracování media.
        this.emitter.emit('progress', donwnloadMediaFunc.length, idx + 1);
      }
    });

    if (donwnloadMediaFunc.length === 0) {
      return [];
    }

    this.emitter.emit('start', donwnloadMediaFunc.length);
    // Sériové stažení assetů pro jednotlivá média.
    await PromiseUtil.allSerial(donwnloadMediaFunc);

    // Navázání média na stažené assety.
    await ContentInfoParser.linkAssetsToMedia(
      this.viewerRegistry,
      this.store,
      contentInfo
    );

    this.emitter.emit('finished');

    if (downloadAssetErrors.length !== 0) {
      this.logger.error(
        new AggregateError(
          downloadAssetErrors,
          'some assets failed to download'
        )
      );
    }

    return assetArray;
  }

  /**
   * Stáhne jeden soubor z nového obsahu.
   * @private
   * @param {IContentInfo} contentInfo
   * @param {Medium} medium
   * @param {ViewerType} viewerType
   * @returns {Promise<Asset, Error>}
   */
  async downloadAsset(contentInfo, medium, viewerType) {
    const remoteUrl = medium.url;
    try {
      const { defaultSize } = this;
      this.logger.debug(`Downloader#downloadAsset start ${remoteUrl}`);
      const extract = viewerType.isExtractable();
      const logicalUnit = await FileSystemUtil.find(
        this.fileSystemManager,
        this.fileSystemUnitFinder,
        this.logicalUnitMapper,
        // TODO: DRY, Device, Downloader, ReportManager
        contentInfo.communicatorSettings.get('storage.types') ??
          this.session.storage
      );
      if (isNil(logicalUnit)) {
        const error = new Error('No unit found.');
        error.name = ErrorName.NotFoundError;
        throw error;
      }
      const logicalPath = await this.basePathResolver.resolve(logicalUnit);
      const fileSystemUnit = await this.logicalUnitMapper.toFileSystemUnit(
        logicalUnit
      );
      // Detekce dostatečného místa k uložení souboru.
      // Zatím se používá pouze výchozí hodnota z konfigurace.
      // Nezjišťuje se skutečná velikost souboru.
      let notEnoughtSpace = await shouldCleanSpace(defaultSize, fileSystemUnit);
      if (notEnoughtSpace) {
        // Pokud je obsazeno více než 90 % úložiště nebo
        // není dostatek místa.
        await this.cleaner.clean(logicalPath, contentInfo);
        notEnoughtSpace = await shouldCleanSpace(defaultSize, fileSystemUnit);
        if (notEnoughtSpace) {
          // Pokud ani po uvolnění není dostatek místa, vyhodí se chyba,
          // která se zaloguje.
          throw new Error(
            `unable to clean space for unit ${fileSystemUnit.getName()}`
          );
        }
      }

      // Uvolnil se dostatek místa, soubor lze stáhnout.
      // Persistentní uložení souboru do hlavního datového skladu.
      return await this.assetDownloader.download(logicalPath, medium, {
        extract
      });
    } finally {
      this.logger.debug(`Downloader#downloadAsset end ${remoteUrl}`);
    }
  }

  /** @type {EventBus['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {EventBus['on']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na požadavek zastavení aktuálně přehrávaného obsahu.
   * @private
   */
  onCleanerBeforeRemoveSomeAssets() {
    this.emitter.emit('cleanerBeforeRemoveSomeAssets');
  }
}

/**
 * @typedef {Object} EventTypes
 * @property {CleanerBeforeRemoveSomeAssetsEventListener} cleanerBeforeRemoveSomeAssets
 * @property {DownloadStartEventListener} start
 * @property {DownloadFinishedEventListener} finished
 * @property {DownloadProgressEventListener} progress
 */

/**
 * @callback DownloadStartEventListener
 * @param {number} total
 * @returns {void}
 */

/**
 * @callback DownloadFinishedEventListener
 * @returns {void}
 */

/**
 * @callback DownloadProgressEventListener
 * @param {number} total
 * @param {number} progress
 * @returns {void}
 */

/**
 * @param {Medium} medium
 * @param {Logger} logger
 * @returns {Promise<Response>}
 */
async function headRequestMedium(medium, logger) {
  const headers = getHeadersForMedium(medium);

  const getRequest = FileTransferUtil.createGetRequest(medium.url, {
    headers
  });
  const headRequest = await FileTransferUtil.createHeadRequest(getRequest);
  const response = await FileTransferUtil.fetchHeadBeforeDownload(headRequest);
  return response;
}

/**
 * Vyfiltruje jen ty media, které jsou stažitelná a nejsou stažena,
 * nebo jsou stažitelná a mají zapnutou kontrolu verze.
 * @param {MediaWithViewerType} media
 * @returns {MediaWithViewerType}
 */
export function filterMediaToDownload(media) {
  return media.filter(
    ({ medium, viewerType }) =>
      // Medium je stažitelné.
      Boolean(viewerType?.isOfflineable()) &&
      // Medium není stažené a nebo je vyžadována kontrola
      (isNil(medium.asset) || Boolean(viewerType?.getCheckFile(medium)))
  );
}

/**
 * Vyfiltruje media bez assetů.
 * @param {MediaWithViewerType} media
 * @returns {MediaWithViewerType}
 */
export function filterMediaWithoutAssets(media) {
  return media.filter(({ medium }) => isNil(medium.asset));
}

/**
 * Vyfiltruje media s assety a zapnutou volnou checkVersion. Odflitrují se media s duplicitní URL.
 * Všechny widgety mají tuto volbu zapnutou.
 * @param {MediaWithViewerType} media
 * @returns {MediaWithViewerType}
 */
export function filterMediaWithAssetAndCheckVersion(media) {
  return media.filter(
    ({ medium, viewerType }) => medium.asset && viewerType?.getCheckFile(medium)
  );
}

/**
 * Vyfiltruje media s duplicitní URL. Typický se jedná
 * o widgety s různými parametry.
 * @param {MediaWithViewerType} media
 * @returns {MediaWithViewerType}
 */
export function filterDuplicatedMedia(media) {
  const urls = new Set();
  return media.filter(({ medium }) => {
    if (urls.has(medium.url)) {
      return false;
    }
    urls.add(medium.url);
    return true;
  });
}

/**
 * Provede HEAD request na všechna media, a vyfiltruje jen ty,
 * která jsou zastarlá tj. vrací 200.
 * @param {MediaWithViewerType} media
 * @param {Logger} logger
 * @returns {Promise<MediaWithViewerType>}
 */
export async function mustFilterOutdatedMedia(media, logger) {
  /** @type {Error[]} */
  const headErrors = [];
  /** @type {{medium: Medium, response: Response | null, viewerType: ViewerType | null}[]} */
  const possiblyOutdatedMedia = await PromiseUtil.allParallel([
    ...media.map(({ medium, viewerType }) => async () => {
      /** @type {Response | null} */
      let response = null;
      try {
        response = await headRequestMedium(medium, logger);
        if (response.status !== 200) {
          if (response.status !== 304) {
            const error = new RangeError(
              `Invalid HTTP status "${response.status}".`
            );
            // V tomto případě nechceme posílat stack trace.
            error.stack = '';
            headErrors.push(error);
          }
          response = null;
        }
      } catch (err) {
        const headError = new Error(
          `HEAD request failed: ${/** @type {Error} */ (err).message}`
        );
        // V tomto případě nechceme posílat stack trace.
        headError.stack = '';
        headErrors.push(headError);
      }
      return {
        medium,
        response,
        viewerType
      };
    })
  ]).finally(() => {
    if (headErrors.length > 0) {
      logger.error(new AggregateError(headErrors, 'some HEAD requests failed'));
    }
  });

  return possiblyOutdatedMedia
    .filter(({ response }) => response !== null)
    .map(({ medium, viewerType }) => ({
      medium,
      viewerType
    }));
}

/**
 * Zjistí, jestli je potřeba uvolnit místo v úložišti.
 * @param {number} fileSize
 * @param {FileSystemUnit} fileSystemUnit
 * @returns {Promise<boolean>} True, pokud je obsazené více než 90 % úložiště
 * nebo velikost souboru je vetší než je volné místo v úložišti.
 */
export async function shouldCleanSpace(fileSize, fileSystemUnit) {
  const detected = await FileSystemSpaceDetector.detect(
    fileSystemUnit,
    fileSize
  );
  const { total, used } = await fileSystemUnit.getInfo();
  return !detected || used / total >= 0.9;
}
